import { describeError } from "@/utils/error-message"
export type MobilePushStatus =
  | "checking"
  | "unsupported"
  | "permission-required"
  | "permission-blocked"
  | "registering"
  | "ready"
  | "degraded"
  | "disabled"

export type MobilePushStage = "service-worker" | "public-key" | "subscription" | "registration" | "disable"

const stageLabels: Record<MobilePushStage, string> = {
  "service-worker": "The notification service worker did not become ready",
  "public-key": "Kettle could not load notification configuration",
  subscription: "The browser did not create a notification subscription",
  registration: "Kettle could not register this browser for notifications",
  disable: "Kettle could not disable notifications for this browser",
}

export class MobilePushError extends Error {
  constructor(
    readonly stage: MobilePushStage,
    message: string,
    readonly retryable = true,
    readonly original?: unknown,
  ) {
    super(message)
    this.name = "MobilePushError"
  }
}

export function pushError(stage: MobilePushStage, cause: unknown) {
  if (cause instanceof MobilePushError) return cause
  const detail = describeError(cause)
  return new MobilePushError(stage, detail ? `${stageLabels[stage]}: ${detail}` : stageLabels[stage], true, cause)
}

export function withPushRequestTimeout<T>(
  request: (signal: AbortSignal) => Promise<T>,
  stage: MobilePushStage,
  timeoutMs: number,
) {
  const controller = new AbortController()
  return withPushTimeout(request(controller.signal), stage, timeoutMs, () => controller.abort())
}

export function withPushTimeout<T>(
  promise: Promise<T>,
  stage: MobilePushStage,
  timeoutMs: number,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let timedOut = false
    const timer = setTimeout(() => {
      timedOut = true
      onTimeout?.()
      reject(new MobilePushError(stage, `${stageLabels[stage]}. Try again.`, true))
    }, timeoutMs)
    promise.then(
      (value) => {
        clearTimeout(timer)
        if (!timedOut) resolve(value)
      },
      (cause) => {
        clearTimeout(timer)
        if (!timedOut) reject(pushError(stage, cause))
      },
    )
  })
}

export function createPushMutationQueue() {
  let pending: Promise<unknown> = Promise.resolve()
  return {
    run<T>(mutation: () => Promise<T>) {
      const result = pending.then(mutation, mutation)
      pending = result.then(() => undefined, () => undefined)
      return result
    },
  }
}

export function createPushOperationGuard() {
  let generation = 0
  return {
    begin() {
      generation += 1
      return generation
    },
    current(operation: number) {
      return operation === generation
    },
    snapshot() {
      return generation
    },
    invalidate() {
      generation += 1
    },
  }
}

export function shouldRepairPushSubscription(
  permission: NotificationPermission,
  enabledPreference: boolean | undefined,
) {
  return permission === "granted" && enabledPreference !== false
}

export function idlePushStatus(
  supported: boolean,
  permission: NotificationPermission,
  enabledPreference: boolean | undefined,
  hasSubscription: boolean,
): MobilePushStatus {
  if (!supported) return "unsupported"
  if (permission === "denied") return "permission-blocked"
  if (hasSubscription) return "ready"
  if (enabledPreference === false) return "disabled"
  return "permission-required"
}
